import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiFilter } from 'react-icons/fi';
import styled from 'styled-components';
import {
  DATA_FONT_FAMILY,
  TITLE_FONT_FAMILY,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/constants';
import {
  EMPTY_CELL_LABEL,
  isColumnFilterActive,
  NUMERIC_FILTER_OPERATORS,
  TEXT_FILTER_OPERATORS,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/column-filter';
import {
  ColumnFilter,
  FilterOperator,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';

interface ColumnFilterMenuProps {
  filter?: ColumnFilter;
  isNumeric: boolean;
  label: string;
  onChange: (filter: ColumnFilter | null) => void;
  values: string[];
}

const MENU_WIDTH = 260;
const MENU_MARGIN = 8;

export function ColumnFilterMenu({
  filter,
  isNumeric,
  label,
  onChange,
  values,
}: ColumnFilterMenuProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [search, setSearch] = useState(``);
  const isOpen = position !== null;
  const isActive = isColumnFilterActive(filter);
  const operators = isNumeric
    ? NUMERIC_FILTER_OPERATORS
    : TEXT_FILTER_OPERATORS;
  const operator = filter?.operator ?? ``;
  const selectedValues = filter?.selectedValues ?? values;
  const visibleValues = values.filter((value) =>
    (value || EMPTY_CELL_LABEL).toLowerCase().includes(search.toLowerCase()),
  );

  const close = () => {
    setPosition(null);
    setSearch(``);
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !menuRef.current?.contains(target) &&
        !buttonRef.current?.contains(target)
      ) {
        close();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === `Escape`) {
        close();
      }
    };
    const handleReposition = (event: Event) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setPosition(getMenuPosition());
      }
    };

    document.addEventListener(`pointerdown`, handlePointerDown);
    document.addEventListener(`keydown`, handleKeyDown);
    window.addEventListener(`scroll`, handleReposition, true);
    window.addEventListener(`resize`, handleReposition);

    return () => {
      document.removeEventListener(`pointerdown`, handlePointerDown);
      document.removeEventListener(`keydown`, handleKeyDown);
      window.removeEventListener(`scroll`, handleReposition, true);
      window.removeEventListener(`resize`, handleReposition);
    };
  }, [isOpen]);

  const getMenuPosition = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) {
      return null;
    }

    const maxLeft = window.innerWidth - MENU_WIDTH - MENU_MARGIN;
    return {
      left: Math.max(MENU_MARGIN, Math.min(rect.left, maxLeft)),
      top: rect.bottom + 4,
    };
  };

  const toggleMenu = () => {
    if (isOpen) {
      close();
      return;
    }

    setPosition(getMenuPosition());
  };

  const emitChange = (nextFilter: ColumnFilter) => {
    onChange(isColumnFilterActive(nextFilter) ? nextFilter : null);
  };

  const updateRule = (changes: Partial<ColumnFilter>) => {
    const nextFilter = { ...filter, ...changes };
    if (!nextFilter.operator && changes.ruleValue !== undefined) {
      nextFilter.operator = operators[0].value;
    }
    onChange(
      isColumnFilterActive(nextFilter) || nextFilter.operator
        ? nextFilter
        : null,
    );
  };

  const toggleValue = (value: string) => {
    const nextValues = selectedValues.includes(value)
      ? selectedValues.filter((selected) => selected !== value)
      : [...selectedValues, value];
    emitChange({
      ...filter,
      selectedValues:
        nextValues.length === values.length ? undefined : nextValues,
    });
  };

  const selectVisible = (isSelected: boolean) => {
    const visibleSet = new Set(visibleValues);
    const nextValues = isSelected
      ? Array.from(new Set([...selectedValues, ...visibleValues]))
      : selectedValues.filter((value) => !visibleSet.has(value));
    emitChange({
      ...filter,
      selectedValues:
        nextValues.length === values.length ? undefined : nextValues,
    });
  };

  const areAllVisibleSelected =
    visibleValues.length > 0 &&
    visibleValues.every((value) => selectedValues.includes(value));

  const stopHeaderInteraction = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <>
      <FilterButton
        ref={buttonRef}
        type="button"
        aria-label={`Filter ${label} column`}
        aria-expanded={isOpen}
        $isActive={isActive}
        onClick={(event) => {
          event.stopPropagation();
          toggleMenu();
        }}
        onKeyDown={stopHeaderInteraction}
        onPointerDown={stopHeaderInteraction}
      >
        <FiFilter />
      </FilterButton>
      {isOpen &&
        createPortal(
          <Menu
            ref={menuRef}
            role="dialog"
            aria-label={`Filter ${label}`}
            style={{ left: position.left, top: position.top }}
            onClick={stopHeaderInteraction}
            onKeyDown={stopHeaderInteraction}
            onPointerDown={stopHeaderInteraction}
          >
            <Section>
              <SectionTitle>
                {isNumeric ? `Number filters` : `Text filters`}
              </SectionTitle>
              <Select
                aria-label="Filter rule"
                value={operator}
                onChange={(event) =>
                  updateRule({
                    operator: (event.target.value || undefined) as
                      | FilterOperator
                      | undefined,
                  })
                }
              >
                <option value="">Choose a rule…</option>
                {operators.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              {operator && (
                <RuleInputs>
                  <Input
                    aria-label="Filter value"
                    placeholder="Value"
                    value={filter?.ruleValue ?? ``}
                    onChange={(event) =>
                      updateRule({ ruleValue: event.target.value })
                    }
                  />
                  {operator === `between` && (
                    <Input
                      aria-label="Filter end value"
                      placeholder="And"
                      value={filter?.ruleValueEnd ?? ``}
                      onChange={(event) =>
                        updateRule({ ruleValueEnd: event.target.value })
                      }
                    />
                  )}
                </RuleInputs>
              )}
            </Section>
            <Section>
              <SectionTitle>Values</SectionTitle>
              <Input
                aria-label="Search values"
                placeholder="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <ValueList>
                <CheckboxRow>
                  <input
                    type="checkbox"
                    checked={areAllVisibleSelected}
                    onChange={(event) => selectVisible(event.target.checked)}
                  />
                  <span>{search ? `Select filtered` : `Select all`}</span>
                </CheckboxRow>
                {visibleValues.map((value) => (
                  <CheckboxRow key={value}>
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(value)}
                      onChange={() => toggleValue(value)}
                    />
                    <span>{value || EMPTY_CELL_LABEL}</span>
                  </CheckboxRow>
                ))}
                {!visibleValues.length && <EmptyState>No matches</EmptyState>}
              </ValueList>
            </Section>
            <Footer>
              <ClearButton
                type="button"
                disabled={!isActive && !filter?.operator}
                onClick={() => onChange(null)}
              >
                Clear filter
              </ClearButton>
              <DoneButton type="button" onClick={close}>
                Done
              </DoneButton>
            </Footer>
          </Menu>,
          document.body,
        )}
    </>
  );
}

const FilterButton = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 2.2rem;
  width: 2.2rem;
  height: 2.2rem;
  padding: 0;
  border: 0;
  border-radius: 0.4rem;
  background: ${({ $isActive }) => ($isActive ? `#e0e7ff` : `transparent`)};
  color: ${({ $isActive }) => ($isActive ? `#4338ca` : `#9ca3af`)};
  font-size: 1.2rem;
  cursor: pointer;
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
  transition: opacity 150ms ease, background-color 150ms ease;

  th:hover &,
  th:focus-within &,
  &[aria-expanded='true'] {
    opacity: 1;
  }

  &:hover {
    background: ${({ $isActive }) => ($isActive ? `#c7d2fe` : `#e5e7eb`)};
  }
`;

const Menu = styled.div`
  position: fixed;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  width: ${MENU_WIDTH}px;
  padding: 1.2rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.8rem;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
  color: #111;
  font-family: ${TITLE_FONT_FAMILY};
  font-size: 1.3rem;
  font-weight: 400;
  text-align: left;
  cursor: default;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const SectionTitle = styled.span`
  color: rgb(119 129 139);
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const controlStyles = `
  box-sizing: border-box;
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid #d1d5db;
  border-radius: 0.4rem;
  background: #fff;
  color: #111;
  font-family: ${DATA_FONT_FAMILY};
  font-size: 1.3rem;
`;

const Select = styled.select`
  ${controlStyles}
`;

const Input = styled.input`
  ${controlStyles}
`;

const RuleInputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const ValueList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 18rem;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.4rem;
`;

const CheckboxRow = styled.label`
  font-family: ${DATA_FONT_FAMILY};
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.5rem 0.8rem;
  cursor: pointer;
  overflow-wrap: anywhere;

  &:hover {
    background: #f3f4f6;
  }
`;

const EmptyState = styled.span`
  font-family: ${DATA_FONT_FAMILY};
  padding: 0.8rem;
  color: #9ca3af;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ClearButton = styled.button`
  padding: 0.4rem 0.2rem;
  border: 0;
  background: transparent;
  color: #4338ca;
  font-size: 1.3rem;
  cursor: pointer;

  &:disabled {
    color: #9ca3af;
    cursor: default;
  }
`;

const DoneButton = styled.button`
  padding: 0.5rem 1.4rem;
  border: 0;
  border-radius: 0.4rem;
  background: #111827;
  color: #fff;
  font-size: 1.3rem;
  cursor: pointer;
`;
